import { debounce } from "@material-ui/core";
import { useMemo, useRef, useEffect } from "react";

/**
 * A custom hook that wraps a handler function in a debounced function. Uses `useRef` to ensure the debounced function does not change between renders.
 *
 * @param handler A function to be debounced.
 * @param delay   The debounce delay.
 */
export const useDebouncedHandler = <A extends unknown[]>(handler: (...args: A) => void, delay: number) => {
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  return useMemo(() => {
    const func = (...args: A) => {
      handlerRef.current(...args);
    };
    return debounce(func, delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const useAsyncDebouncedHandler = <A extends unknown[]>(
  handler: (...args: A) => Promise<unknown>,
  delay: number,
) => {
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  return useMemo(() => {
    const func = async (...args: A) => {
      await handlerRef.current(...args);
    };
    return debounce(func, delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const useDebouncedEffect = (effect: () => void, deps: unknown[], delay: number) => {
  useEffect(() => {
    const handler = setTimeout(() => effect(), delay);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...(deps || []), delay]);
};
